// ── 상품 설정 섹션 (⚙️ 설정 모달 전용) ─────────────────────────────────────
// (피드백 옵션 블록은 미팅지도 관리 ⚙️ 설정 모달로 이관 — 2026-09-01)
//
// 2026-09-20: 자주 쓰는 것과 가끔 쓰는 것을 갈랐다(사용자 요청).
//   밖(상품 관리 본문)  = 카테고리·상품 만들기·이름 고치기·버리기, 기본상품, 회사에 붙이기
//   여기(⚙️ 설정)       = 유효기간 · 환산장수  ← 가끔 쓰는 것만
//   일일콜 표시 방식은 회사마다 다른 값이라 오른쪽 "회사에 붙이기" 칸으로 옮겼고,
//   기본상품은 찾기 어렵다는 지적에 카테고리 머리줄로 꺼냈다(2026-09-20).
import React from 'react';
import AdminColHeader from '../../../components/AdminColHeader';
import { validityBadge } from './classNames';
import { ib } from './classNames';
import { ProductWeightControl, ProductWeightNotice } from './ProductWeightControl';
import { ProductWeightHistoryModal } from './ProductWeightHistoryModal';
import { useTicketWeightHistory } from './useTicketWeightHistory';

export function ProductsAndFeedbackSection({
  tickets, ticketLoading, categories,
  editWeightId, editWeightValue, setEditWeightValue,
  editWeightDate, setEditWeightDate,
  editWeightMemo, setEditWeightMemo,
  ticketValidityMap, setTicketValidityMap,
  ticketSaving: saving,
  startEditWeight, cancelEditWeight, saveTicketWeight,
  fetchTickets,
  startEditValidity, cancelEditValidity, saveValidity,
}) {
  const weightHistory = useTicketWeightHistory({ onSaved: fetchTickets });

  // 손님 회사(테넌트)는 카테고리 표가 비어 있다 — 상품마다 분류 이름을 직접 적어 쓴다.
  //   그때는 그 분류 이름으로 묶어서 보여준다. 안 그러면 설정 화면이 통째로 비어
  //   유효기간조차 못 고친다(본문에서 뺐으므로).
  const freeCategory = categories.length === 0;
  const groups = freeCategory
    ? [...new Set(tickets.map((t) => String(t.category || '').trim()))]
      .sort()
      .map((name) => ({ id: `free-${name || 'none'}`, name: name || '미분류', free: true, items: tickets.filter((t) => String(t.category || '').trim() === name) }))
    : categories.map((cat) => ({ id: cat.id, name: cat.name, free: false, default_product_id: cat.default_product_id, items: tickets.filter((t) => t.category === cat.name) }));

  return (
    <>
      <section className="bg-white dark:bg-pine-900 border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-[var(--border)]">
          <span className="text-[13px] font-extrabold text-[var(--text)]"><AdminColHeader label="상품 설정" db="ticket_types, product_categories" /></span>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[var(--bg-subtle)] text-[var(--text-muted)]">{tickets.length}개</span>
        </div>
        {!freeCategory && <ProductWeightNotice />}
        <p className="px-3.5 py-2 text-[11px] text-[var(--text-muted)] border-b border-[var(--border)]">
          카테고리·상품 이름과 회사 연결은 설정을 닫고 상품 관리 화면에서 바꿉니다.
        </p>

        {ticketLoading
          ? <div className="px-3.5 py-3 text-xs text-[var(--text-muted)]">불러오는 중...</div>
          : groups.map((cat) => {
            const catTickets = cat.items;
            return (
              <div key={cat.id} className="border-b border-[var(--border)]">
                {/* 머리줄 — 이름은 보기만.
                    기본상품은 찾기 쉽도록 상품 관리 본문 머리줄로 꺼냈다(2026-09-20 사용자 지적). */}
                <div className="flex items-center gap-1.5 px-3.5 py-[7px] bg-[var(--bg-subtle)]">
                  <span className="text-xs font-extrabold text-slate-700 dark:text-slate-200">{cat.name}</span>
                </div>

                {/* 상품 줄 — 환산장수와 유효기간만 */}
                {catTickets.map((t) => {
                  const cur = ticketValidityMap[t.id] || { val: '', editing: false };
                  return (
                    <div key={t.id} className="flex items-center gap-1.5 px-3.5 py-[6px] pl-[22px] border-b border-[var(--bg-subtle)]">
                      <span className="text-xs font-semibold text-[var(--text)] flex-1">{t.name}</span>
                      {/* 환산장수는 회사 구분이 없는 전역 데이터라 운영사 전용(서버 403) — 손님 회사에는 숨긴다. */}
                      {!freeCategory && (
                      <ProductWeightControl
                        ticket={t}
                        isEditing={editWeightId === t.id}
                        editWeightDate={editWeightDate}
                        setEditWeightDate={setEditWeightDate}
                        editWeightValue={editWeightValue}
                        setEditWeightValue={setEditWeightValue}
                        editWeightMemo={editWeightMemo}
                        setEditWeightMemo={setEditWeightMemo}
                        saving={saving}
                        saveTicketWeight={saveTicketWeight}
                        cancelEditWeight={cancelEditWeight}
                        startEditWeight={startEditWeight}
                        openWeightHistory={weightHistory.openWeightHistory}
                      />
                      )}
                      {cur.editing ? (
                        <span className="inline-flex items-center gap-1">
                          <input type="text" inputMode="numeric" autoFocus placeholder="미설정" value={cur.val}
                            onChange={(e) => setTicketValidityMap((prev) => ({ ...prev, [t.id]: { ...prev[t.id], val: e.target.value.replace(/[^0-9]/g, '') } }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveValidity(t.id); if (e.key === 'Escape') cancelEditValidity(t.id); }}
                            className="w-12 border border-accent-500 rounded-md px-1 py-0.5 text-xs font-bold text-accent-700 dark:text-accent-300 outline-none text-right bg-white dark:bg-pine-800" />
                          <span className="text-[11px] text-[var(--text-muted)]">개월</span>
                          <button className={ib('ok')} onClick={() => saveValidity(t.id)} disabled={saving}>✓</button>
                          <button className={ib('cancel')} onClick={() => cancelEditValidity(t.id)}>✕</button>
                        </span>
                      ) : (
                        <span className={validityBadge(!!cur.val)} onClick={() => startEditValidity(t.id)} title="유효기간 수정">
                          {cur.val ? `${cur.val}개월` : '유효기간 미설정'}
                        </span>
                      )}
                    </div>
                  );
                })}
                {catTickets.length === 0 && (
                  <div className="px-3.5 py-[6px] pl-[22px] text-[11px] text-[var(--text-muted)]">이 카테고리에 상품이 없습니다.</div>
                )}
              </div>
            );
          })
        }
      </section>
      <ProductWeightHistoryModal
        ticket={weightHistory.activeWeightTicket}
        rows={weightHistory.weightHistoryRows}
        loading={weightHistory.weightHistoryLoading}
        saving={weightHistory.weightHistorySaving}
        form={weightHistory.weightHistoryForm}
        onFormChange={weightHistory.updateWeightHistoryForm}
        onSave={weightHistory.saveWeightHistory}
        onClose={weightHistory.closeWeightHistory}
      />
    </>
  );
}
